import { PRODUCT_TYPES, ProductType } from '@white-label-configuration/constants';
import { Airport, Airline, Terminal, CustomersOptions } from '@white-label-types/component';
import { AirlineVisibilityRules, BaggageVisibilityRules, DiscountVisibilityRules, SearchCriteriaConfig } from '@white-label-types/search-box';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { SearchBoxFormData } from '../search-box';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';

/**
 * Defines a field in the search box configuration.
 */
export type SearchBoxFieldDefinition = {
  name: string;
  show: boolean | undefined;
  excludeFromStep3?: boolean;
};

/**
 * Create a search box configuration based on the current form data and search step.
 * @param formData The form data for the search box.
 * @param step The current step in the search process.
 * @returns The configuration for the search box.
 */
export const createSearchBoxConfig = (formData: SearchBoxFormData, step: 'step1' | 'step2' | 'step3') => {
  const primaryProductType = getAppVariable<ProductType>('primary_product_type', PRODUCT_TYPES.PARKING);
  const isParking = (step === 'step1' || step === 'step2') && primaryProductType === PRODUCT_TYPES.PARKING;
  const isLounges = (step === 'step1' || step === 'step2') && primaryProductType === PRODUCT_TYPES.LOUNGES;
  const isTimeslot = getFeatureFlag('NGQ_11_TimeSlot', false);

  const layout = (() => {
    if (step !== 'step1') return 'horizontal';
    return isParking ? 'horizontal' : 'vertical';
  })();

  const airports = getAppVariable<Airport[]>('pois', []);
  const allTerminals = getAppVariable<Terminal[]>('terminals', []);
  const airlines = getAppVariable<Airline[]>('products.lounges.airlines_list', []);

  const defaultVisibilityRules = {
    is_enabled_in_landing_page: false,
    is_enabled_in_results_page: false,
    is_enabled_in_payment_page: false,
  }
  const airlinesVisibilityRules = getAppVariable<AirlineVisibilityRules>('airlines_visibility_rules', defaultVisibilityRules);
  const baggageVisibilityRules = getAppVariable<BaggageVisibilityRules>('baggage_visibility_rules', defaultVisibilityRules);
  const discountVisibilityRules = getAppVariable<DiscountVisibilityRules>('discount_visibility_rules', defaultVisibilityRules);

  const searchCriteriaConfig = getAppVariable<SearchCriteriaConfig>('search_criteria', {} as SearchCriteriaConfig);
  const hasOneDatePicker = (() => {
    if (isParking) return false;
    if (!searchCriteriaConfig) return true;
    return searchCriteriaConfig.picker_type === 'one';
  })();
  const hasTwoDatePickers = !hasOneDatePicker;

  const customerGroups = (() => {
    const showExtrasGuestsOnHomePage = isParking && getAppVariable('is_sell_bundles_enabled');
    const primaryProductCustomerGroups = getAppVariable<CustomersOptions | null>(`products.${primaryProductType}.customer_groups`, null);
    const extrasProductsCustomerGroups = getAppVariable<CustomersOptions | null>(`products.lounges.customer_groups`, null)
      ?? getAppVariable<CustomersOptions | null>(`products.fasttrack.customer_groups`, null);
    if (step === 'step1' || step === 'step2') {
      if (showExtrasGuestsOnHomePage) {
        return extrasProductsCustomerGroups;
      }
      return primaryProductCustomerGroups;
    } else if (step === 'step3') {
      return extrasProductsCustomerGroups;
    } else {
      return primaryProductCustomerGroups;
    }
  })();

  const customerGroupsExists = (() => {
    return Boolean(
      customerGroups
      && Object.keys(customerGroups).length > 0
    );
  })();

  const airportTerminals: Terminal[] = getAirportTerminals(airports, allTerminals, formData);

  if (isTimeslot) {
    // Timeslot has a completely different search box configuration that does not overlap with other product types (yet)
    return {
      showPassengersSelector: true,
      showDepartureDate: true,
      showFlightSearch: true,
    }
  } else {
    // All other product types share the same pool of fields
    return {
      layout,
      airports,
      hasOneDatePicker,
      hasTwoDatePickers,
      enableDatePickerSwap: isLounges && hasTwoDatePickers,
      showAirlineSelector: airlinesVisibilityRules.is_enabled_in_landing_page
        && airlines.length > 0,
      showAirportSelector: airports.length > 1,
      showBaggageSelector: baggageVisibilityRules.is_enabled_in_landing_page,
      showCustomersSelector: customerGroupsExists,
      showDiscountField: discountVisibilityRules.is_enabled_in_landing_page  && !isLounges,
      showTerminalSelect: airportTerminals.length > 1,
    };
  }

}

/**
 * Get the search box fields based on the current form data and search step.
 * @param formData The form data for the search box.
 * @param step The current step in the search process.
 * @returns The fields to be displayed in the search box.
 */
export const getSearchBoxFields = (formData: SearchBoxFormData, step: 'step1' | 'step2' | 'step3', explicitEcludes?: []) => {
  const searchBoxConfig = createSearchBoxConfig(formData, step);
  const SEARCH_BOX_FIELDS: Record<string, SearchBoxFieldDefinition> = {
    airport: {
      name: 'airport',
      show: searchBoxConfig.showAirportSelector,
      excludeFromStep3: true,
    },
    airline: {
      name: 'airline',
      show: searchBoxConfig.showAirlineSelector,
      excludeFromStep3: true,
    },
    baggage: {
      name: 'baggage',
      show: searchBoxConfig.showBaggageSelector,
      excludeFromStep3: true,
    },
    terminal: {
      name: 'terminal',
      show: searchBoxConfig.showTerminalSelect,
    },
    datePicker1: {
      name: 'datePicker1',
      show: searchBoxConfig.hasOneDatePicker || searchBoxConfig.hasTwoDatePickers,
      excludeFromStep3: true,
    },
    datePicker2: {
      name: 'datePicker2',
      show: searchBoxConfig.hasTwoDatePickers,
      excludeFromStep3: true,
    },
    departureDate: {
      name: 'departureDate',
      show: searchBoxConfig.showDepartureDate,
    },
    discount: {
      name: 'discount',
      show: searchBoxConfig.showDiscountField,
      excludeFromStep3: true,
    },
    flightSearch: {
      name: 'flightSearch',
      show: searchBoxConfig.showFlightSearch,
    },
    guests: {
      name: 'guests',
      show: searchBoxConfig.showCustomersSelector,
    },
    passengers: {
      name: 'passengers',
      show: searchBoxConfig.showPassengersSelector
    },
  };
  const enabledFields = Object.values(SEARCH_BOX_FIELDS).filter(
    (field) => !explicitEcludes?.includes(field.name) && field.show && !(step === 'step3' && field.excludeFromStep3)
  );

  return enabledFields;
}

/**
 * Returns the list of terminals for the selected airport, filtered by allTerminals.
 * Extracted for testability.
 */
export function getAirportTerminals(
  airports: Airport[],
  allTerminals: Terminal[],
  formData: { airport?: string }
): Terminal[] {
  let defaultAirport;
  if (airports.length === 1 && !formData.airport) {
    defaultAirport = airports[0].code;
  }
  const airportTerminals = airports.find(
    (item) => item.code === (formData.airport || defaultAirport)
  )?.terminals;
  if (airportTerminals) {
    return airportTerminals.filter((t1) =>
      allTerminals.some((t2) => t1.id === t2.id)
    );
  } else {
    return [];
  }
}
